import { calendarEventListItemsInWindow } from '@/js/core/utils/calendar-event-projections';
import { isDisplayEventExpired } from '@/js/core/utils/event-expiry';
import { formatDateKey } from '@/js/core/utils/formatters';
import { DEFAULT_TIMEZONE } from '@/js/core/utils/timezone';

export { isDisplayEventExpired };

/**
 * Fallback for if the server doesn't provide a value.'
 */
export const DEFAULT_UPCOMING_DAYS = 7;

/**
 * The rows the board should show at `now`: projected into the lookahead window, then cleared of
 * anything finished. Called once per pagination cycle, so a row never vanishes mid-slide.
 *
 * The endpoint attaches every occurrence of a matching event, so this window is what keeps distant
 * ones off the panel: an occurrence outside it is dropped, and one spanning the edge is clamped to
 * the part that falls inside. Recomputed from `now` each time, so later occurrences arrive as the
 * window rolls forward.
 *
 * @param {Array<object>} events
 * @param {string} timezone
 * @param {number} now  Epoch milliseconds.
 * @param {{ upcomingDays?: number }} options
 * @returns {Array<object>}
 */
export function displayEventRows(
  events,
  timezone = DEFAULT_TIMEZONE,
  now = Date.now(),
  { upcomingDays = DEFAULT_UPCOMING_DAYS } = {}
) {
  const startKey = formatDateKey(now, timezone);
  const endKey = addDaysToDateKey(startKey, Math.max(1, upcomingDays) - 1);

  return calendarEventListItemsInWindow(events, { startKey, endKey, timezone }).filter(
    event => !isDisplayEventExpired(event, timezone, now)
  );
}

/**
 * Whether a row's date range falls on tomorrow. Mirrors the projection's `isToday` rule but lives
 * here so the shared calendar projection stays untouched. A range covering both days counts as
 * today's, and callers render the TODAY tag in preference.
 *
 * @param {{ start?: { dateKey?: string }, end?: { dateKey?: string } }} rangeInfo
 * @param {string} timezone
 * @param {number} now  Epoch milliseconds.
 * @returns {boolean}
 */
export function isDisplayEventTomorrow(rangeInfo, timezone = DEFAULT_TIMEZONE, now = Date.now()) {
  const startKey = rangeInfo?.start?.dateKey;
  const endKey = rangeInfo?.end?.dateKey || startKey;
  const tomorrowKey = addDaysToDateKey(formatDateKey(now, timezone), 1);

  return Boolean(startKey && endKey && tomorrowKey >= startKey && tomorrowKey <= endKey);
}

/**
 * Shift a `YYYY-MM-DD` key by a number of days, anchored at midday UTC so a daylight saving
 * shift cannot move the result onto the neighbouring date.
 */
function addDaysToDateKey(dateKey, days) {
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

  date.setUTCDate(date.getUTCDate() + days);

  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('-');
}
